import { ReactNode, useMemo, useEffect, useState } from 'react'
import {
  ThemeProvider as MuiThemeProvider,
  CssBaseline,
  createTheme,
  useMediaQuery,
} from '@mui/material'
import { useUIStore } from '@store/uiStore'
import { useAuthStore } from '@store/authStore'
import { autoThemeIsDark } from '@utils/sunAltitude'
import { themeRegistry } from '@themes/registry'
import { useMyThemes } from '@hooks/useThemes'
import { buildThemeConfig, themeConfigFromPalettes } from '@themes/buildTheme'

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { themeName, themeMode, themeCache } = useUIStore()
  const { data: serverThemes } = useMyThemes()
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const autoDark = useAutoDark(themeMode === 'auto', prefersDark)
  const effectiveMode: 'light' | 'dark' =
    themeMode === 'system'
      ? prefersDark ? 'dark' : 'light'
      : themeMode === 'auto'
        ? autoDark ? 'dark' : 'light'
        : themeMode

  const theme = useMemo(() => {
    // Register the user's server themes into the registry HERE, synchronously, before we read
    // it below. Doing this in a post-render useEffect (as it was) populated the registry one
    // step too late: this memo had already resolved and fallen back to `adventure`, and
    // mutating the singleton registry isn't React state — so it never re-ran the memo. The
    // selected theme stayed on the fallback until some unrelated dependency (a light/dark
    // toggle) forced a recompute. Registering in-render also avoids a flash of the default
    // theme on every load.
    if (serverThemes) {
      themeRegistry.clearServerThemes()
      for (const dto of serverThemes) {
        themeRegistry.register(buildThemeConfig(dto))
      }
    } else if (themeCache && themeCache.id === themeName && !themeRegistry.getTheme(themeName)) {
      // Server themes haven't arrived yet. Seed the registry from the last-known colors of the
      // selected theme so this first render uses the real theme instead of flashing `adventure`.
      // When the fetch lands, clearServerThemes() above replaces this with the authoritative one.
      themeRegistry.register(themeConfigFromPalettes(themeName, themeCache.light, themeCache.dark))
    }

    // Get theme config from registry, fallback to adventure
    const themeConfig = themeRegistry.getTheme(themeName) || themeRegistry.getTheme('adventure')!

    // Select light or dark mode (system mode is already resolved into effectiveMode)
    const themeOptions = effectiveMode === 'dark' ? themeConfig.dark : themeConfig.light

    // Create MUI theme
    return createTheme(themeOptions)
  }, [themeName, effectiveMode, serverThemes, themeCache])

  // If the user's selected theme was revoked server-side, persist a fallback. This writes to
  // the store, so it stays in an effect (never mutate state during render). It runs after the
  // memo above, so the registry already reflects the freshly-registered server themes.
  useEffect(() => {
    if (serverThemes && !themeRegistry.getTheme(themeName)) {
      useUIStore.getState().setTheme('adventure')
    }
  }, [serverThemes, themeName])

  // Persist the selected theme's colors so the next load can paint it before the server themes
  // fetch (and before React even mounts — see the inline script in index.html). Compare against
  // the stored snapshot so this only writes when the palette actually changes, not every render.
  useEffect(() => {
    const config = themeRegistry.getTheme(themeName)
    const light = config?.light.palette
    const dark = config?.dark.palette
    if (!light || !dark) return
    const prev = useUIStore.getState().themeCache
    const changed =
      !prev ||
      prev.id !== themeName ||
      JSON.stringify(prev.light) !== JSON.stringify(light) ||
      JSON.stringify(prev.dark) !== JSON.stringify(dark)
    if (changed) {
      useUIStore.getState().setThemeCache({ id: themeName, light, dark })
    }
  }, [themeName, serverThemes])

  // Expose effective mode on the document so plain CSS files can branch on it
  useEffect(() => {
    document.documentElement.dataset.themeMode = effectiveMode
    if (useUIStore.getState().lastEffectiveMode !== effectiveMode) {
      useUIStore.getState().setLastEffectiveMode(effectiveMode)
    }
  }, [effectiveMode])

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  )
}

/**
 * Auto mode: dark while the sun is below 6° at the home location, re-checked every minute. With no
 * home location there is no sun to follow, so it behaves like System.
 */
function useAutoDark(enabled: boolean, prefersDark: boolean): boolean {
  const lat = useAuthStore((s) => s.user?.homeLatitude)
  const lng = useAuthStore((s) => s.user?.homeLongitude)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!enabled) return
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [enabled])

  if (!enabled) return false
  if (lat == null || lng == null) return prefersDark
  return autoThemeIsDark(new Date(now), lat, lng)
}
